import { defineComponent as _defineComponent } from 'vue'
import { vShow as _vShow, withDirectives as _withDirectives, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementBlock as _createElementBlock, renderSlot as _renderSlot, unref as _unref, Transition as _Transition, withCtx as _withCtx, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-1b1d4f28"),n=n(),_popScopeId(),n)
const _hoisted_1 = { id: "topLevelLayout" }
const _hoisted_2 = { class: "flexConstraint" }
const _hoisted_3 = { class: "contentContainer" }
const _hoisted_4 = {
  key: 1,
  class: "pushPrompt"
}
const _hoisted_5 = { class: "bodyConstraint" }
const _hoisted_6 = { class: "mainArea" }
const _hoisted_7 = { class: "contentArea" }
const _hoisted_8 = {
  key: 0,
  class: "privacyMode"
}
const _hoisted_9 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "message" }, [
  /*#__PURE__*/_createElementVNode("h1", null, "Privacy mode is enabled"),
  /*#__PURE__*/_createElementVNode("p", null, "Disable privacy mode on the top bar to access your content")
], -1))
const _hoisted_10 = [
  _hoisted_9
]
const _hoisted_11 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { id: "portal-dock-panel-target" }, null, -1))

import { computed, ComputedRef, defineAsyncComponent, Ref, ref } from 'vue';

import ChatSummaryDialog from '@/components/dialogs/ChatSummaryDialog.vue';
import { ViewableEntry } from '@/components/files/files.types';
import PushNotificationPermissionBanner from '@/components/pushmessaging/PushNotificationPermissionBanner.vue';
import { useRoute } from '@/composables/router/VueRouter';
import { AppName } from '@/data/Branding';
import BrowserDetection from '@/data/BrowserDetection';
import { Track } from '@/data/datatypes/Track';
import HeaderBar from '@/HeaderBar.vue';
import NonAppMenu from '@/NonAppMenu.vue';
import RouteNames from '@/router/RouteNames';
import { useKnowledgeBaseStore } from '@/stores/KnowledgeBase';
import { usePushNotificationStore } from '@/stores/PushNotification';
import { useTrackEntriesStore } from '@/stores/TrackEntries';
import { useTracksStore } from '@/stores/Tracks';
import { useUIStateStore } from '@/stores/UIState';
import { useUserStore } from '@/stores/User';

interface Props {
  inPhoneLandscapeMode?: boolean;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'TopLevelLayout',
  props: {
    inPhoneLandscapeMode: { type: Boolean, default: false }
  },
  setup(__props: any) {

const FileViewDialog = defineAsyncComponent(() => import('@/components/dialogs/FileViewDialog.vue'));
const ConfirmDialog = defineAsyncComponent(() => import('@/components/dialogs/ConfirmDialog.vue'));

const tracksStore = useTracksStore();
const trackEntriesStore = useTrackEntriesStore();
const userStore = useUserStore();
const uiStateStore = useUIStateStore();
const pushNotificationStore = usePushNotificationStore();
const knowledgeBaseStore = useKnowledgeBaseStore();

const route = useRoute();



const showClearAllNotifications: Ref<boolean> = ref(false);

const pushNotificationStillDenied: ComputedRef<boolean> = computed(() => {
  return pushNotificationStore.pushNotificationStillDenied;
});

function clearPushNotificationStillDenied(): Promise<void> {
  return pushNotificationStore.clearPushNotificationStillDenied();
}

const privacyMode: ComputedRef<boolean> = computed(() => {
  return uiStateStore.privacyMode;
});

const hideHeaderBar: ComputedRef<boolean> = computed(() => {
  return uiStateStore.hideHeaderBar;
});

const sideMenuOpen: ComputedRef<boolean> = computed(() => {
  return uiStateStore.sideMenuOpen;
});

const isGuestUser: ComputedRef<boolean> = computed(() => {
  return userStore.isGuestUser;
});

const currentlyDisplayedChatSummaryTrack: ComputedRef<Track | null> = computed(() => {
  return tracksStore.currentlyDisplayedChatSummaryTrack;
});

const currentlyDisplayedFile: ComputedRef<ViewableEntry | null> = computed(() => {
  return trackEntriesStore.currentlyDisplayedFile;
});

const isMobile: ComputedRef<boolean> = computed(() => {
  return BrowserDetection.isMobile();
});

const showFooterMenu: ComputedRef<boolean> = computed(() => {
  return isMobile.value && BrowserDetection.isSupportedBrowser();
});

const pushNotificationStillDeniedMsg: ComputedRef<string> = computed(() => {
  return `${AppName} cannot sent push notifications to this device. To allow ${AppName} to push notifications,' +
    ' update your browser notification settings.`;
});

function setCurrentlyDisplayedChatSummaryTrack(track: Track | null): void {
  return tracksStore.setCurrentlyDisplayedChatSummaryTrack(track);
}

function clearAllNotifications(): void {
  showClearAllNotifications.value = false;
  tracksStore.clearAllUnseenTrackActivity();
  knowledgeBaseStore.clearAllKnowledgeBaseNotifications();
}

const isServerMessage: ComputedRef<boolean> = computed(() => {
  return route.name === RouteNames.SERVER_MESSAGE_ROUTE_NAME;
});

const showPushPermissionRequest: ComputedRef<boolean> = computed(() => {
  if (isGuestUser.value) {
    return false;
  }

  // If the browser doesn't support notifications, or it was dismissed on a previous app load (flagged in local
  // storage) don't show the banner.
  if (pushNotificationStore.pushNotificationState === null ||
      !pushNotificationStore.pushNotificationState.supported ||
      pushNotificationStore.pushBannerDismissed) {
    return false;
  }

  // Only show the permissions required banner if the user has notifications enabled (in their CafeX settings)
  // for the current device type.
  const isMobileDevice = BrowserDetection.isMobile();
  const userWantsNotificationsForThisDeviceType = userStore.currentUser &&
    ((userStore.currentUser.chatMobilePushNotifications && isMobileDevice) ||
    (userStore.currentUser.chatDesktopPushNotifications && !isMobileDevice));

  // If their settings suggest they want notifications here, show the banner if we need to
  if (userWantsNotificationsForThisDeviceType) {
    return !pushNotificationStore.pushBannerDismissed && !pushNotificationStore.pushNotificationState.enabled;
  } else {
    return false;
  }
});

function setCurrentlyDisplayedFile(file: ViewableEntry | null): void {
  trackEntriesStore.setCurrentlyDisplayedFile(file);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        (!isGuestUser.value && !isServerMessage.value)
          ? _withDirectives((_openBlock(), _createBlock(HeaderBar, {
              key: 0,
              onClearAllNotifications: _cache[0] || (_cache[0] = ($event: any) => (showClearAllNotifications.value = true))
            }, null, 512)), [
              [_vShow, !hideHeaderBar.value]
            ])
          : _createCommentVNode("", true),
        (showPushPermissionRequest.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createVNode(PushNotificationPermissionBanner)
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("div", _hoisted_7, [
              _renderSlot(_ctx.$slots, "content"),
              _createVNode(_Transition, {
                name: "fade",
                mode: "out-in"
              }, {
                default: _withCtx(() => [
                  (currentlyDisplayedFile.value)
                    ? (_openBlock(), _createBlock(_unref(FileViewDialog), {
                        key: 0,
                        onClose: _cache[1] || (_cache[1] = ($event: any) => (setCurrentlyDisplayedFile(null))),
                        entry: currentlyDisplayedFile.value
                      }, null, 8, ["entry"]))
                    : _createCommentVNode("", true)
                ], undefined, true),
                _: 1
              }),
              _createVNode(_Transition, {
                name: "fade",
                mode: "out-in"
              }, {
                default: _withCtx(() => [
                  (currentlyDisplayedChatSummaryTrack.value)
                    ? (_openBlock(), _createBlock(ChatSummaryDialog, {
                        key: 0,
                        track: currentlyDisplayedChatSummaryTrack.value,
                        onClose: _cache[2] || (_cache[2] = ($event: any) => (setCurrentlyDisplayedChatSummaryTrack(null)))
                      }, null, 8, ["track"]))
                    : _createCommentVNode("", true)
                ], undefined, true),
                _: 1
              })
            ])
          ]),
          (privacyMode.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, _hoisted_10))
            : _createCommentVNode("", true)
        ]),
        (showFooterMenu.value)
          ? _withDirectives((_openBlock(), _createElementBlock("div", {
              key: 2,
              class: _normalizeClass(["footer", {sideMenuOpen: sideMenuOpen.value}])
            }, [
              _createVNode(NonAppMenu, {
                onClearAllNotifications: _cache[3] || (_cache[3] = ($event: any) => (showClearAllNotifications.value = true)),
                "is-header": false
              })
            ], 2)), [
              [_vShow, !hideHeaderBar.value]
            ])
          : _createCommentVNode("", true),
        (showClearAllNotifications.value)
          ? (_openBlock(), _createBlock(_unref(ConfirmDialog), {
              key: 3,
              onCancel: _cache[4] || (_cache[4] = ($event: any) => (showClearAllNotifications.value = false)),
              onConfirm: clearAllNotifications,
              isDangerousOperation: true,
              bodyText: "This will clear all of your notifications"
            }))
          : _createCommentVNode("", true),
        (pushNotificationStillDenied.value)
          ? (_openBlock(), _createBlock(_unref(ConfirmDialog), {
              key: 4,
              headerText: 'Check Browser Settings',
              bodyText: pushNotificationStillDeniedMsg.value,
              confirmOnly: true,
              onConfirm: clearPushNotificationStillDenied
            }, null, 8, ["bodyText"]))
          : _createCommentVNode("", true),
        _hoisted_11
      ])
    ])
  ]))
}
}

})